import React from 'react';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import { Text } from '@newcore/ui/typography';
import { LoginCard } from '@/components/login-card';
import { LoginButton } from '@/components/ui/login-button';
import { OtpInput } from '@/components/ui/otp-input';
import { Page } from '@/components/ui/page';
import { StatusMessage } from '@/components/ui/status-message';
import { useLanguage } from '@/contexts/language-context';
import { useOtpForm } from '@/hooks/forms/use-otp-form';
import type { UseMfaOrchestratorReturn } from '@/hooks/use-mfa-orchestrator.types';
import { useMfaResend } from '@/hooks/use-mfa-resend';
import {
  useResendCountdown,
  useResendDeadline,
} from '@/hooks/use-resend-countdown';
import { resendOtp } from '@/services/apis/otp.api';

interface EmailOtpFactorProps {
  submitResponse: UseMfaOrchestratorReturn['submitResponse'];
  emailObfuscated?: string;
  nextResendAt?: string;
  hasAlternatives?: boolean;
  onShowAlternatives?: () => void;
  username?: string;
  onBack?: () => void;
  isEscalationFlow?: boolean;
}

export const EmailOtpFactor: React.FC<EmailOtpFactorProps> = ({
  submitResponse,
  emailObfuscated,
  nextResendAt,
  hasAlternatives,
  onShowAlternatives,
  username,
  onBack,
  isEscalationFlow,
}) => {
  const { t } = useLanguage();

  const {
    form,
    onSubmit,
    isSubmitting,
    getErrorMessage,
    hasExceededRetries,
    resetAttempts,
  } = useOtpForm({
    submitOtp: submitResponse,
    maxRetries: 3,
  });

  const { deadline, setDeadline, isResolving } =
    useResendDeadline(nextResendAt);
  const { canResend, isExhausted, formattedTime } =
    useResendCountdown(deadline);

  const { resend, isResending } = useMfaResend({
    resendRequest: resendOtp,
    onResendSuccess: result => {
      setDeadline(result);
      resetAttempts();
    },
  });

  const { setValue, watch, handleSubmit, clearErrors } = form;
  const codeValue = watch('code');

  const handleCodeChange = (newCode: string[]) => {
    // Clear any previous errors when user starts editing
    clearErrors();
    setValue('code', newCode, { shouldValidate: true });
  };

  const handleComplete = async () => {
    try {
      await handleSubmit(onSubmit)();
    } catch (error) {
      console.error('Email OTP: Auto-submit error:', error);
    }
  };

  const errorMessage = getErrorMessage();
  const hasError = Boolean(errorMessage);

  return (
    <Page className='flex flex-col justify-center items-center'>
      <LoginCard
        hasAlternatives={hasAlternatives}
        onShowAlternatives={onShowAlternatives}
        username={username}
        onBack={onBack}
        isEscalationFlow={isEscalationFlow}
      >
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-1'>
            <Text as='h1' size={TextSizes.MD} weight={FontWeights.BOLD}>
              Verify using Email
            </Text>
            <Text size={TextSizes.SM} className='text-secondary'>
              {emailObfuscated
                ? `Enter the 6-digit code sent to ${emailObfuscated}`
                : 'Enter the 6-digit code sent to your email'}
            </Text>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className='flex flex-col gap-4'>
              <OtpInput
                value={codeValue}
                onChange={handleCodeChange}
                onComplete={handleComplete}
                hasError={hasError}
                disabled={isSubmitting || hasExceededRetries}
                autoFocus
                length={6}
              />

              {hasError && <StatusMessage>{errorMessage}</StatusMessage>}

              <LoginButton
                data-testid='submit-email-otp-button'
                type='submit'
                size={Sizes.LG}
                className='w-full'
                isLoading={isSubmitting}
                disabled={hasExceededRetries}
              >
                {isSubmitting ? t('auth.signingIn') : t('common.continue')}
              </LoginButton>

              <div className='flex flex-col gap-2 items-center mt-2'>
                <Text
                  as='p'
                  size={TextSizes.SM}
                  weight={FontWeights.MEDIUM}
                  className='text-center'
                >
                  {t('mfa.didntReceiveIt')}
                </Text>
                {!isResolving &&
                  (canResend ? (
                    <LoginButton
                      type='button'
                      variant={ButtonVariants.LINK_PRIMARY}
                      onClick={resend}
                      disabled={isResending}
                    >
                      {t('mfa.resendCode')}
                    </LoginButton>
                  ) : (
                    <Text
                      as='p'
                      size={TextSizes.SM}
                      className='text-secondary text-center'
                    >
                      {isExhausted
                        ? t('mfa.resendLimitReached')
                        : `${t('mfa.requestNewCodeIn')} ${formattedTime}`}
                    </Text>
                  ))}
              </div>
            </div>
          </form>
        </div>
      </LoginCard>
    </Page>
  );
};
